import loadable from '@loadable/component';
import { useMemo, useState } from 'react';

import { Box, Grid, Heading, Text, useTheme } from '@simplywallst/ui-core';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import RewardSolid from '@simplywallst/ui-core/icons/RewardSolid';
import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';

import { pluralise } from '@/utilities/pluralise';

import { Tooltip as SnowflakeTooltip } from './Tooltip';

import type { AggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';

const InformationModal = loadable(() => import('./InformationModal'));

interface Props {
  aggregatePortfolio: AggregatePortfolio;
}

const getCount = (count: number | null) => {
  if (count === null) return '0';
  if (count > 99) return '+99';
  return String(count);
};

export const AggregatePortfolioSnowflake = ({ aggregatePortfolio }: Props) => {
  const theme = useTheme();

  const [showInformationModal, setShowInformationModal] = useState(false);

  const normalisedPoints: [number, number, number, number, number] =
    useMemo(() => {
      const { value, future, past, health, dividend } =
        aggregatePortfolio.snowflake.scores;

      return [
        (value || 0) + 1,
        (future || 0) + 1,
        (past || 0) + 1,
        (health || 0) + 1,
        (dividend || 0) + 1,
      ];
    }, [aggregatePortfolio.snowflake.scores]);

  const { risksCount, rewardsCount, holdingsCount } =
    aggregatePortfolio.analysis;

  const holdingOrHoldings = pluralise(holdingsCount, 'Holding', 'Holdings');

  const hasRisks = risksCount !== null;
  const hasRewards = rewardsCount !== null;

  return (
    <>
      <Grid
        templateColumns="auto min-content"
        templateRows="24px"
        alignItems="center"
      >
        <Heading as="h2" typography="smallStrong" color="white">
          Portfolio Snowflake
        </Heading>
        <button onClick={() => setShowInformationModal(true)}>
          <InformationSolid fill={theme.color.whiteRGBA(0.5)} />
        </button>
        {showInformationModal && (
          <InformationModal onClose={() => setShowInformationModal(false)} />
        )}
      </Grid>
      <Grid placeContent="center">
        <Box width={220} data-cy-id="snowflake-overview">
          <Snowflake points={normalisedPoints}>
            <Snowflake.Labels />
            <Snowflake.Segments>
              <SnowflakeTooltip
                section="dividend"
                aggregatePortfolio={aggregatePortfolio}
              >
                <Snowflake.DividendSegment />
              </SnowflakeTooltip>
              <SnowflakeTooltip
                section="past"
                aggregatePortfolio={aggregatePortfolio}
              >
                <Snowflake.PastSegment />
              </SnowflakeTooltip>
              <SnowflakeTooltip
                section="value"
                aggregatePortfolio={aggregatePortfolio}
              >
                <Snowflake.ValueSegment />
              </SnowflakeTooltip>
              <SnowflakeTooltip
                section="health"
                aggregatePortfolio={aggregatePortfolio}
              >
                <Snowflake.HealthSegment />
              </SnowflakeTooltip>
              <SnowflakeTooltip
                section="future"
                aggregatePortfolio={aggregatePortfolio}
              >
                <Snowflake.FutureSegment />
              </SnowflakeTooltip>
            </Snowflake.Segments>
          </Snowflake>
        </Box>
      </Grid>
      <Grid
        templateRowsLg="auto"
        templateRowsMd="auto auto"
        templateRows="auto"
        templateColumnsLg="max-content auto"
        templateColumnsMd="auto"
        templateColumns="max-content auto"
        justifyContent="start"
        alignItems="center"
        gap="16px"
      >
        <Text typography="xSmall" color="whiteRGBA" opacity={0.7}>
          {`${aggregatePortfolio.analysis.holdingsCount} ${holdingOrHoldings}`}
        </Text>
        {hasRisks || hasRewards ? (
          <Grid templateColumns="auto auto" gap="8px">
            {hasRisks && (
              <Box
                borderRadius="x0_5"
                style={{
                  backgroundColor: theme.color.badRGBA(0.1),
                }}
              >
                <Grid
                  padding="x0_5"
                  paddingRight="x1_5"
                  paddingLeft="x1"
                  borderRadius="x0_5"
                  templateColumns="max-content max-content"
                  alignItems="center"
                  gap="2px"
                  data-cy-id="portfolio-risks"
                >
                  <Box position="relative" width={24} height={24}>
                    <Box
                      position="absolute"
                      backgroundColor="white"
                      borderRadius="x3"
                      width={12}
                      height={12}
                      transform="translate(50%, 50%)"
                    />
                    <Box position="relative">
                      <WarningSolid fill={theme.color.bad} />
                    </Box>
                  </Box>
                  <Text typography="xSmall">
                    {`${getCount(risksCount)} ${pluralise(
                      risksCount,
                      'Risk',
                      'Risks'
                    )}`}
                  </Text>
                </Grid>
              </Box>
            )}
            {hasRewards && (
              <Box
                borderRadius="x0_5"
                style={{
                  backgroundColor: theme.color.goodRGBA(0.1),
                }}
              >
                <Grid
                  padding="x0_5"
                  paddingRight="x1_5"
                  paddingLeft="x1"
                  borderRadius="x0_5"
                  templateColumns="max-content max-content"
                  alignItems="center"
                  data-cy-id="portfolio-rewards"
                >
                  <RewardSolid fill={theme.color.good} />
                  <Text typography="xSmall">
                    {`${getCount(rewardsCount)} ${pluralise(
                      rewardsCount,
                      'Reward',
                      'Rewards'
                    )}`}
                  </Text>
                </Grid>
              </Box>
            )}
          </Grid>
        ) : null}
      </Grid>
    </>
  );
};
